import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import React from "react";
import { AnimatePresence, m } from "framer-motion";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems } from "~/features/app/utils";
import { getSubNavThemeStyle } from "~/features/sub-nav/sub-nav-theme";
import { SubNavLink } from "~/features/sub-nav/sub-nav-link";
import { Icon } from "~/features/ui/icon";
import CaretUp from "~/public/icons/caret-up.svg";
import CaretDown from "~/public/icons/caret-down.svg";
import { Portal } from "~/features/ui/portal";
import { useSubNavLayout } from "~/features/sub-nav/context";
const hoverStyles = {
  light: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(208 208 218 / var(--tw-bg-opacity))"
      }
    }
  },
  dark: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(92 92 92 / var(--tw-bg-opacity))"
      }
    }
  }
} as const;
const fragment = graphql(`
  fragment SubNavLinks on subNavLinks_NeoField {
    ...SubNavLink
  }
`);
type Props = {
  currentPageTitle: string;
  navLinks: FragmentType<typeof fragment>[];
};
export function SubNavLinks(props: Props) {
  const {
    navLinks,
    currentPageTitle,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const links = useFragment(fragment, navLinks);
  const {
    subNavMode
  } = useSubNavLayout();
  const themeStyle = getSubNavThemeStyle(subNavMode);
  if (!hasItems(links)) {
    return null;
  }
  return <_StyledRadixDropdownMenuRoot onOpenChange={open => setIsOpen(open)} {...rest}>
      <AnimatePresence>
        {isOpen && <Portal targetId="sub-nav">
            <_StyledMDiv initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          ease: "easeInOut",
          duration: 0.25
        }} />
          </Portal>}
      </AnimatePresence>
      <RadixDropdownMenu.Trigger asChild>
        <_StyledButton aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"} $_css={[{
        "display": "flex",
        "height": "100%",
        "width": "100%",
        "alignItems": "center",
        "justifyContent": "space-between",
        "columnGap": "2.4rem",
        "borderBottomWidth": "1px",
        "borderRightWidth": "1px",
        "paddingLeft": "2.4rem",
        "paddingRight": "2.4rem",
        "@media (min-width: 1024px)": {
          "width": "auto",
          "minWidth": "300px",
          "paddingLeft": "4rem",
          "paddingRight": "4rem"
        }
      }, {
        "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
        "transitionDuration": "250ms",
        "transitionTimingFunction": "ease-in-out"
      }, themeStyle.border, hoverStyles[subNavMode]]}>
          <span>{currentPageTitle}</span>
          <Icon icon={isOpen ? CaretUp : CaretDown} />
        </_StyledButton>
      </RadixDropdownMenu.Trigger>
      <_StyledRadixDropdownMenuContent align="start" sideOffset={1} $_css2={[{
      "zIndex": "10",
      "display": "flex",
      "width": "var(--radix-popper-available-width)",
      "flexDirection": "column",
      "borderBottomWidth": "1px",
      "@media (min-width: 1024px)": {
        "width": "var(--radix-popper-anchor-width)",
        "borderRightWidth": "1px"
      }
    }, {
      "@keyframes radix-dropdown-menu-collapse-keyframes": {
        "from": {
          "transform": "scaleY(1)"
        },
        "to": {
          "transform": "scaleY(0)"
        }
      },
      "&[data-state=\"closed\"]": {
        "animation": "radix-dropdown-menu-collapse-keyframes 250ms ease-in-out 250ms"
      },
      "@keyframes radix-dropdown-menu-expand-keyframes": {
        "from": {
          "transform": "scaleY(0)"
        },
        "to": {
          "transform": "scaleY(1)"
        }
      },
      "&[data-state=\"open\"]": {
        "animation": "radix-dropdown-menu-expand-keyframes 250ms ease-in-out"
      }
    }, {
      "transformOrigin": "var(--radix-dropdown-menu-content-transform-origin)"
    }, themeStyle.border, themeStyle.background]}>
        {links.map((link, idx) => <_StyledRadixDropdownMenuItem style={{
        transitionDelay: isOpen ? `${idx * 80 + 250}ms` : "0ms"
      }} key={idx} $_css3={[{
        "opacity": "0",
        "transitionProperty": "opacity",
        "transitionDuration": "250ms",
        "transitionTimingFunction": "ease-in-out",
        "&:not(:last-child)": {
          "borderBottomWidth": "1px"
        }
      }, isOpen && {
        "opacity": "1"
      }, themeStyle.border]}>
            <SubNavLink {...link} />
          </_StyledRadixDropdownMenuItem>)}
      </_StyledRadixDropdownMenuContent>
    </_StyledRadixDropdownMenuRoot>;
}
var _StyledRadixDropdownMenuRoot = _styled(RadixDropdownMenu.Root).withConfig({
  componentId: "sc-sf-720auh-0"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400"
});
var _StyledMDiv = _styled2(m.div).withConfig({
  componentId: "sc-sf-720auh-1"
})({
  "position": "fixed",
  "inset": "0",
  "zIndex": "-1",
  "backgroundColor": "rgba(0, 7, 4, 0.6)",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledButton = _styled3("button").withConfig({
  componentId: "sc-sf-720auh-2"
})`${p => p.$_css}`;
var _StyledRadixDropdownMenuContent = _styled4(RadixDropdownMenu.Content).withConfig({
  componentId: "sc-sf-720auh-3"
})`${p => p.$_css2}`;
var _StyledRadixDropdownMenuItem = /*#__PURE__*/_styled5(RadixDropdownMenu.Item).withConfig({
  displayName: "sub-nav-links___StyledRadixDropdownMenuItem",
  componentId: "sc-1f3t5lv-0"
})(["", ""], p => p.$_css3);